import type { ReactNode } from "react";
import { MdOutlineClose } from "react-icons/md";
import MeritoButton from "../../components/common/MeritoButtons/MeritoButtons";
import { getStateInfo } from "./gstStateInfo";
import styles from "../../pages/GSTReconciliation/GSTReconciliation.module.scss";

export interface GSTAccountRecord {
  key: string;
  gstin: string;
  username?: string;
  state?: string;
  fetchedFromPan?: boolean;
  isReconnect?: boolean;
  showDelete?: boolean;
  deleting?: boolean;
  disableDelete?: boolean;
  renderStatus: (record: GSTAccountRecord) => ReactNode;
  renderRowDetail?: (record: GSTAccountRecord) => ReactNode;
  renderSubStatus?: (record: GSTAccountRecord) => ReactNode;
  renderUpload?: (record: GSTAccountRecord) => ReactNode;
  onReconnect?: () => void;
  onDelete?: () => void;
}

interface GSTAccountListProps {
  records: GSTAccountRecord[];
  headerLabel?: string;
  emptyState?: ReactNode;
}

export default function GSTAccountList({
  records,
  headerLabel,
  emptyState,
}: GSTAccountListProps) {
  if (records.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  return (
    <div className={styles.gstinListBox}>
      <div className={styles.gstinListHeader}>
        {headerLabel ?? `${records.length} GSTIN${records.length !== 1 ? "s" : ""}`}
      </div>
      <div className={styles.gstinListScroll}>
        {records.map((record) => {
          const state = getStateInfo(record.gstin);
          return (
            <div key={record.key} id={record.key} className={styles.gstinListRowWrapper}>
              <div className={styles.gstinListRow}>
                <div className={styles.gstinRowLeft}>
                  <div
                    className={`${styles.gstinAvatar} ${
                      record.isReconnect ? styles.gstinAvatarError : ""
                    }`}
                  >
                    {state.abbr}
                  </div>
                  <div className={styles.gstinRowInfo}>
                    <div className={styles.gstinNumberRow}>
                      <div className={styles.gstinNumber}>{record.gstin}</div>
                      {record.fetchedFromPan && (
                        <span className={styles.fetchedFromPanTag}>
                          Fetched automatically
                        </span>
                      )}
                    </div>
                    <div className={styles.gstinMeta}>
                      <span className={styles.gstinMetaText}>
                        {record.state || state.name}
                      </span>
                      {record.username && (
                        <>
                          <span className={styles.gstinMetaDot} />
                          <span className={styles.gstinMetaText}>{record.username}</span>
                        </>
                      )}
                    </div>
                    {record.renderSubStatus && (
                      <div className={styles.gstinSubError}>
                        {record.renderSubStatus(record)}
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.gstinRowRight}>
                  {record.renderStatus(record)}
                  {record.renderUpload && record.renderUpload(record)}
                  <MeritoButton
                    type="tertiary"
                    size="md"
                    onlyIcon
                    icon={<MdOutlineClose />}
                    loading={record.deleting}
                    disabled={!record.showDelete || record.disableDelete}
                    onClick={record.onDelete}
                    extraStyles={record.showDelete ? undefined : { visibility: "hidden" }}
                  />
                </div>
              </div>
              {record.renderRowDetail ? (
                <div className={styles.gstinRowDetail}>
                  {record.renderRowDetail(record)}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
